/**
 * RevenueTrendChart — a year-over-year monthly revenue line chart (Recharts).
 *
 * Renders the CURRENT year vs the PREVIOUS year as two lines over 12 months,
 * from the real `/api/analytics/revenue-trend` data. Money arrives as
 * integer-minor STRINGS; we convert to major units (÷100) only for display and
 * tooltip formatting.
 *
 * Client-only: this component is imported solely by the console client bundle
 * (never the SSR AppShell), so Recharts never runs during `renderToString`.
 * The container has a fixed height so ResponsiveContainer can measure it.
 */
import type { ReactElement } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import type { RevenueTrendPoint } from "../services.js";

/** Brand hues used by the chart. */
const FOREST = "#1D35A9";
const GOLD = "#98A2B3";
const SLATE = "#667085";
const RULE = "#EAECF0";

/** A charting row with numeric major-unit values Recharts can plot. */
interface TrendDatum {
  readonly label: string;
  readonly thisYear: number;
  readonly lastYear: number;
}

/** Compact INR formatter for axis + tooltip (e.g. ₹1.2L, ₹45k). */
function formatCompact(major: number): string {
  if (major >= 10_000_000) return `₹${(major / 10_000_000).toFixed(1)}Cr`;
  if (major >= 100_000) return `₹${(major / 100_000).toFixed(1)}L`;
  if (major >= 1_000) return `₹${(major / 1_000).toFixed(0)}k`;
  return `₹${major.toFixed(0)}`;
}

/** Full INR formatter for the tooltip body. */
function formatFull(major: number): string {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(major);
  } catch {
    return `₹${major.toFixed(0)}`;
  }
}

/** The custom tooltip, styled to the brand (emerald card). */
function TrendTooltip({
  active,
  payload,
  label,
}: {
  readonly active?: boolean;
  readonly payload?: readonly { readonly dataKey?: string; readonly value?: number }[];
  readonly label?: string;
}): ReactElement | null {
  if (active !== true || payload === undefined || payload.length === 0) {
    return null;
  }
  const ty = payload.find((p) => p.dataKey === "thisYear")?.value ?? 0;
  const ly = payload.find((p) => p.dataKey === "lastYear")?.value ?? 0;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip__title">{label}</p>
      <p className="chart-tooltip__row">
        <span className="chart-tooltip__swatch" style={{ background: GOLD }} />
        This year: <strong>{formatFull(ty)}</strong>
      </p>
      <p className="chart-tooltip__row">
        <span className="chart-tooltip__swatch" style={{ background: SLATE }} />
        Last year: <strong>{formatFull(ly)}</strong>
      </p>
    </div>
  );
}

/** The revenue year-over-year line chart. */
export function RevenueTrendChart({
  points,
}: {
  readonly points: readonly RevenueTrendPoint[];
}): ReactElement {
  const data: TrendDatum[] = points.map((p) => ({
    label: p.label,
    thisYear: Number(p.thisYearMinor) / 100,
    lastYear: Number(p.lastYearMinor) / 100,
  }));

  return (
    <div className="chart-frame" style={{ height: 300 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
          <CartesianGrid stroke={RULE} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: SLATE, fontSize: 12 }}
            axisLine={{ stroke: RULE }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={formatCompact}
            tick={{ fill: SLATE, fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            width={56}
          />
          <Tooltip content={<TrendTooltip />} cursor={{ stroke: RULE }} />
          <Legend
            verticalAlign="top"
            align="right"
            iconType="plainline"
            wrapperStyle={{ fontSize: 12, color: SLATE }}
          />
          <Line
            name="Last year"
            type="monotone"
            dataKey="lastYear"
            stroke={SLATE}
            strokeWidth={1.5}
            strokeDasharray="4 4"
            dot={false}
            activeDot={{ r: 3, fill: SLATE }}
          />
          <Line
            name="This year"
            type="monotone"
            dataKey="thisYear"
            stroke={GOLD}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 4, fill: GOLD, stroke: FOREST, strokeWidth: 1 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default RevenueTrendChart;
